import { useEffect, useState, useMemo } from "react";
import { api, formatDate } from "@/lib/diningApi";
import { useAuth } from "@/context/diningAuth";
import { useWaves } from "@/context/DiningWaveContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function Attendance() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor" || user?.role === "operator";
  const { currentWave } = useWaves();
  const [events, setEvents] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [drafts, setDrafts] = useState({}); // { [eventId]: [n1, n2, ...] }
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    const [rs, es, ats] = await Promise.all([
      api.get("/restaurants").then(({ data }) => data),
      api.get("/events").then(({ data }) => data),
      api.get("/attendance").then(({ data }) => data),
    ]);
    setRestaurants(rs); setEvents(es);
    const map = {};
    ats.forEach((a) => { map[a.event_id] = a; });
    setAttendance(map);
  };
  useEffect(() => { load(); }, []);

  const restMap = useMemo(() => Object.fromEntries(restaurants.map(r => [r.id, r])), [restaurants]);

  const setDraft = (eid, idx, val) => {
    setDrafts((prev) => {
      const arr = [...(prev[eid] || [])];
      arr[idx] = val;
      return { ...prev, [eid]: arr };
    });
  };

  const save = async (e) => {
    const sessions = e.sessions || 1;
    const existing = attendance[e.id]?.attendees_by_session || [];
    const arr = drafts[e.id] || [];
    const final = [];
    for (let i = 0; i < sessions; i++) {
      const v = arr[i];
      const fallback = existing[i] || 0;
      final.push(Number(v != null && v !== "" ? v : fallback));
    }
    try {
      await api.post("/attendance", { event_id: e.id, attendees_by_session: final });
      toast.success(`Asistencia guardada (${final.reduce((a,b)=>a+b,0)} pax)`);
      load();
      setDrafts((prev) => ({ ...prev, [e.id]: [] }));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // Group all events by date, sorted strictly chronologically (oldest → newest)
  // Only show confirmed or confirmed_late events (skip provisionals).
  const byDate = useMemo(() => {
    const map = {};
    events
      .filter((e) => e.status === "confirmed" || e.status === "confirmed_late")
      .forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return Object.entries(map)
      .map(([date, evts]) => ({
        date,
        events: evts.sort((a,b) => (a.meal_type || "").localeCompare(b.meal_type || "")),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [events]);

  const today = new Date().toISOString().slice(0, 10);

  const EventRow = ({ e }) => {
    const r = restMap[e.restaurant_id];
    const sessions = e.sessions || 1;
    const paxPerTurn = r?.pax_per_session || 0;
    const att = attendance[e.id];
    return (
      <div className="px-6 py-4 border-t border-zinc-100 hover:bg-zinc-50">
        <div className="grid grid-cols-12 gap-3 items-center text-sm">
          <div className="col-span-4 font-medium">{r?.name || e.restaurant_id}</div>
          <div className="col-span-2">{e.meal_type === "lunch" ? "Comida" : "Cena"} <span className="font-mono text-zinc-500">x{sessions}</span></div>
          <div className="col-span-2 font-mono text-zinc-500">Aforo/turno {paxPerTurn || "—"}</div>
          <div className="col-span-3 font-mono">
            {att ? `Total ${att.attendees_real}` : <span className="text-zinc-400">— sin cargar</span>}
          </div>
          <div className="col-span-1 flex justify-end">
            {canEdit && <Button onClick={() => save(e)} size="sm" data-testid={`attendance-save-${e.id}`} className="rounded-none bg-zinc-900 hover:bg-zinc-800">Guardar</Button>}
          </div>
        </div>
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: `repeat(${Math.min(sessions, 6)}, minmax(0, 1fr))`, maxWidth: "60%" }}>
          {Array.from({ length: sessions }).map((_, idx) => (
            <div key={idx}>
              <p className="label-eyebrow mb-1">Turno {idx + 1}</p>
              <Input
                type="number" min="0"
                placeholder={String(att?.attendees_by_session?.[idx] ?? "0")}
                value={drafts[e.id]?.[idx] ?? ""}
                onChange={(ev) => setDraft(e.id, idx, ev.target.value)}
                className="rounded-none h-9"
                data-testid={`attendance-input-${e.id}-${idx}`}
                disabled={!canEdit}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const toggle = (d) => setCollapsed({ ...collapsed, [d]: !collapsed[d] });

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="attendance-page">
      <div className="mb-8">
        <p className="label-eyebrow">Carga diaria · {currentWave?.name}</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Asistencia</h1>
        <p className="text-sm text-zinc-600 mt-3 max-w-2xl">Carga cuántas personas acudieron a cada turno, agrupado por fecha. StreetXO tiene comida y cena por separado.</p>
      </div>

      <div className="space-y-3">
        {byDate.length === 0 && (
          <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500">Sin eventos.</div>
        )}
        {byDate.map(({ date, events: evts }) => {
          const isPast = date <= today;
          const isCollapsed = collapsed[date] ?? !isPast; // past expanded, future collapsed
          const totalLoaded = evts.reduce((s, e) => s + (attendance[e.id]?.attendees_real || 0), 0);
          return (
            <div key={date} className="bg-white border border-zinc-200" data-testid={`date-block-${date}`}>
              <button onClick={() => toggle(date)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-50 text-left">
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-500" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />}
                  <div>
                    <p className="label-eyebrow">{isPast ? "Pasado" : "Próximo"}</p>
                    <p className="font-heading text-xl mt-0.5">{formatDate(date)}</p>
                  </div>
                </div>
                <div className="flex gap-8 text-right">
                  <div>
                    <p className="label-eyebrow">Sesiones</p>
                    <p className="font-mono text-base mt-0.5">{evts.length}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Cargado</p>
                    <p className="font-mono text-base mt-0.5">{totalLoaded || "—"}</p>
                  </div>
                </div>
              </button>
              {!isCollapsed && evts.map((e) => <EventRow key={e.id} e={e} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
